import { PERMISSIONS } from './permissions.js';
/**
 * Permisiuni PERSONALE: nu vin cu niciun rol, nici măcar cu `admin`.
 *
 * Există fiindcă modelul obișnuit nu putea exprima cererea lui Victor (29.09.2026): exportul și
 * datele necenzurate de client să fie doar ale lui. O permisiune obișnuită n-ar fi restrâns nimic
 * — adminul le primește pe toate din rol, iar în firmă sunt patru admini.
 *
 * Trei reguli, toate necesare ca să însemne ceva:
 *  1. nu se acordă din rol (`PERMISSIONS` are listă goală) — vezi `resolveEffectiveAccess`;
 *  2. nu apar în catalogul de pagini, deci nu se pot lua prin accesul la o pagină;
 *  3. se acordă și se retrag DOAR de cine le are deja — vezi ruta de acordare.
 *
 * A treia e cea care le ține personale. Fără ea, orice admin și-ar fi putut da singur dreptul, iar
 * primele două ar fi fost decor.
 */
export const PERMISIUNI_PERSONALE = [
    'date.clienti:export',
    'tasks.privat:monitor',
    'tasks.export:token',
];
/** Capabilități care NU se deleghează niciodată din pagina de acces (rămân doar Admin). */
export const BLOCKED_PERMISSIONS = [
    'settings.users:read',
    'settings.users:write',
    'settings.users:delete',
    'audit:read',
    'settings.access:manage',
    'settings.ea:write',
    'anaf:write',
    'settings.emag:write',
    'settings.fan:write',
    'orders:approve',
    'marketing.necesar:aproba',
    'marketing.sampling:texte',
    'marketing.sampling:gdpr',
    'marketing.sampling:delete',
];
export const PAGES = [
    // General
    // Dashboard-ul nu e in matrice: il are oricine e logat (vezi `permissions.ts`).
    {
        key: 'alerts',
        label: 'Centru alerte',
        group: 'Acasă',
        permissions: ['alerts:read', 'alerts:write'],
    },
    {
        key: 'changelog',
        label: 'Ce s-a schimbat',
        group: 'Acasă',
        permissions: ['changelog:read'],
    },
    // Comercial
    {
        key: 'prices',
        label: 'Matrice prețuri & marje',
        group: 'Vânzări',
        permissions: [
            'prices.b2c:read',
            'prices.b2c:write',
            'prices.b2b:read',
            'prices.b2b:write',
            'prices.deals:read',
            'prices.deals:write',
            'prices.margins:read',
        ],
    },
    // Marketing
    {
        key: 'mk_print',
        label: 'Cereri tipografie & design',
        group: 'Marketing',
        permissions: ['marketing.print:read', 'marketing.print:write'],
    },
    {
        // Eticheta urmeaza ecranul, nu istoricul: `mk_promo` deschide „Campanii promo - flux"
        // (/marketing/camppromo), iar `mk_campaigns` deschide „Campanii promo"
        // (/marketing/promo). Erau invers, si adminul bifa pagina gresita.
        key: 'mk_promo',
        label: 'Campanii promo - flux',
        group: 'Marketing',
        permissions: ['marketing.promo:read', 'marketing.promo:write'],
    },
    {
        key: 'mk_campaigns',
        label: 'Campanii promo',
        group: 'Marketing',
        permissions: ['marketing.campaigns:read', 'marketing.campaigns:write'],
    },
    {
        key: 'mk_campaigns_mk',
        label: 'Campanii marketing',
        group: 'Marketing',
        permissions: ['marketing.campaigns_mk:read', 'marketing.campaigns_mk:write'],
    },
    {
        key: 'mk_influencers',
        label: 'Influenceri',
        group: 'Marketing',
        permissions: ['marketing.influencers:read', 'marketing.influencers:write'],
    },
    {
        key: 'mk_events',
        label: 'PR & Evenimente',
        group: 'Marketing',
        permissions: ['marketing.events:read', 'marketing.events:write'],
    },
    {
        // Medici, clinici, farmacii, branduri + mentenanta lunara (task #2516). Se da omului, nu
        // rolului: fiecare isi vede doar colaborarile lui.
        key: 'colaborari',
        label: 'Colaborări',
        group: 'Marketing',
        permissions: ['colaborari:read', 'colaborari:write'],
    },
    {
        key: 'mk_listings',
        label: 'Listări canale',
        group: 'Marketing',
        permissions: ['marketing.listings:read', 'marketing.listings:write'],
    },
    {
        key: 'mk_label',
        label: 'Generator etichete',
        group: 'Marketing',
        permissions: ['marketing.label:read', 'marketing.label:write'],
    },
    {
        key: 'mk_renders',
        label: 'Randări 3D',
        group: 'Marketing',
        permissions: ['marketing.renders:read', 'marketing.renders:write'],
    },
    {
        key: 'mk_sampling',
        label: 'Sampling',
        group: 'Marketing',
        permissions: ['marketing.sampling:read', 'marketing.sampling:write'],
    },
    {
        key: 'mk_sampling_stand',
        label: 'Sampling — vedere de stand',
        group: 'Marketing',
        permissions: ['marketing.sampling:stand'],
    },
    {
        key: 'mk_materials',
        label: 'Materiale marketing',
        group: 'Marketing',
        permissions: ['marketing.materials:read', 'marketing.materials:write'],
    },
    {
        key: 'mk_suppliers',
        label: 'Furnizori',
        group: 'Marketing',
        permissions: ['marketing.suppliers:read', 'marketing.suppliers:write'],
    },
    {
        key: 'mk_shopify',
        label: 'Shopify',
        // Grup propriu, ca in bara laterala. Catalogul il tinea sub Marketing.
        group: 'Magazine online',
        permissions: ['marketing.shopify:read', 'marketing.shopify:write'],
    },
    // Meta (Facebook + Instagram) — grup propriu, ca Shopify si eMag.
    {
        key: 'mk_meta',
        label: 'Meta Ads',
        group: 'Marketing',
        permissions: ['marketing.meta:read', 'marketing.meta:write'],
    },
    // eFactura
    { key: 'anaf', label: 'eFactura (SPV)', group: 'Bani', permissions: ['anaf:read'] },
    // eMag
    {
        key: 'emag',
        label: 'eMAG',
        group: 'Magazine online',
        permissions: ['emag:read', 'emag:write', 'settings.emag:read'],
    },
    // Compliance
    {
        key: 'compliance',
        label: 'Compliance (reconcilieri)',
        group: 'Bani',
        permissions: ['compliance:read', 'compliance:run'],
    },
    {
        key: 'taxe',
        label: 'Taxe & impozite',
        group: 'Bani',
        permissions: ['taxe:read', 'taxe:write'],
    },
    {
        key: 'verificare-cifre',
        label: 'Verificare cifre',
        group: 'Bani',
        permissions: ['verificareCifre:read', 'verificareCifre:write'],
    },
    {
        key: 'ea',
        label: 'Setări ExpertAccounts',
        group: 'Setări',
        permissions: ['settings.ea:read'],
    },
    {
        key: 'fan',
        // Cheia ramane `fan`: pe ea sunt salvate drepturile deja acordate (RolePageAccess /
        // UserPageAccess). Doar eticheta se schimba, fiindca ecranul devine comun mai multor curieri.
        label: 'Rambursuri curieri',
        group: 'Bani',
        permissions: ['fan:read', 'fan:write', 'fan:post', 'settings.fan:read'],
    },
    {
        key: 'corespondenta',
        label: 'Corespondență (asistent AI)',
        group: 'Vânzări',
        permissions: ['corespondenta:read', 'corespondenta:write'],
    },
    // Operațional
    {
        key: 'tasks',
        label: 'Task-uri',
        group: 'Acasă',
        permissions: ['tasks:read', 'tasks:write', 'tasks:delete'],
    },
    {
        key: 'inbox',
        label: 'Inbox',
        group: 'Acasă',
        permissions: ['inbox:read', 'inbox:write'],
    },
    {
        key: 'mail',
        label: 'Mail',
        group: 'Acasă',
        permissions: ['mail:read'],
    },
    {
        key: 'orders',
        label: 'Comenzi',
        group: 'Vânzări',
        permissions: ['orders:read', 'orders:write', 'clienti.acte:write'],
    },
    {
        key: 'agenti',
        // Un singur drept acopera DOUA pagini din meniu: „Tablou de bord vânzări" si
        // „Portofolii agenți (EA)". Eticheta le numeste pe amandoua, ca bifa sa spuna
        // exact ce deschide.
        label: 'Tablou de bord vânzări + Portofolii agenți (EA)',
        group: 'Vânzări',
        permissions: ['operational.agenti:read', 'operational.agenti:write'],
    },
    {
        /*
          Alocarea clientilor, SEPARATA de tabloul de vanzari.
    
          Statea in aceeasi bifa, iar rolul `vanzari` o primise pentru tablou — deci fiecare agent
          vedea si ecranul de mutat clienti intre agenti, cu toti cei 141 de clienti si cu numele
          colegilor (Victor Ciortan, 28.09.2026: „aici nu au de ce sa aiba ei drepturi").
    
          Doua ecrane cu public diferit n-au ce cauta sub o bifa comuna: una din ele ajunge unde nu
          trebuie, si nimeni nu observa pana nu se uita cineva peste umarul unui agent.
        */
        key: 'alocare_clienti',
        label: 'Alocare clienți pe agenți',
        group: 'Vânzări',
        permissions: ['operational.alocare:read', 'operational.alocare:write'],
    },
    {
        key: 'inventar',
        label: 'Diferențe inventar',
        group: 'Operațiuni',
        permissions: ['operational.inventar:read'],
    },
    {
        key: 'intrari',
        label: 'Intrări în gestiune',
        group: 'Operațiuni',
        permissions: ['operational.intrari:read'],
    },
    {
        key: 'expedieri_awb',
        label: 'Expedieri AWB',
        group: 'Operațiuni',
        permissions: ['operational.expedieri:read'],
    },
    {
        key: 'rompetrol',
        label: 'Flotă Rompetrol',
        group: 'Operațiuni',
        permissions: ['operational.rompetrol:read', 'operational.rompetrol:write'],
    },
    {
        key: 'flota_pv',
        label: 'Procese-verbale flotă',
        group: 'Operațiuni',
        permissions: ['operational.flota_pv:read', 'operational.flota_pv:write'],
    },
    {
        key: 'sellout',
        label: 'Sell-out clienți',
        group: 'Vânzări',
        permissions: ['sellout:read', 'sellout:write'],
    },
    {
        key: 'discounturi',
        label: 'Discounturi acordate',
        group: 'Vânzări',
        permissions: ['discounturi:read', 'discounturi:write'],
    },
    {
        /*
          Profitabilitatea, SEPARATA de tabloul de vanzari (`agenti`), desi e o fila a lui: rolul
          `vanzari` are tabloul, iar costurile si profitul sunt pentru Victor, admini si (din
          03.11.2026) managerii de vanzari. O bifa comuna le-ar fi dat agentilor si costurile.
        */
        key: 'profitabilitate',
        label: 'Profitabilitate pe agenți (fila din Tablou de bord vânzări)',
        group: 'Vânzări',
        permissions: ['vanzari.profitabilitate:read', 'vanzari.profitabilitate:write'],
    },
    // Organizare & Juridic
    {
        key: 'meetings',
        label: 'Întâlniri',
        group: 'Firmă & oameni',
        permissions: ['meetings:read', 'meetings:write'],
    },
    {
        key: 'concedii',
        label: 'Concedii',
        group: 'Firmă & oameni',
        permissions: ['hr.concedii:read', 'hr.concedii:write'],
    },
    {
        key: 'onrc',
        label: 'ONRC',
        group: 'Firmă & oameni',
        permissions: ['onrc:read', 'onrc:write'],
    },
    {
        key: 'contracts',
        label: 'Contracte',
        group: 'Firmă & oameni',
        permissions: ['contracts:read', 'contracts:write'],
    },
    {
        key: 'legal',
        label: 'Juridic',
        group: 'Firmă & oameni',
        permissions: ['legal:read', 'legal:write'],
    },
    {
        key: 'lab',
        label: 'Laborator',
        group: 'Firmă & oameni',
        permissions: ['lab:read', 'lab:write'],
    },
    // Resurse umane
    {
        key: 'hr_employees',
        label: 'Angajați',
        group: 'Setări',
        permissions: ['hr.employees:read', 'hr.employees:write'],
    },
    {
        key: 'hr_deconturi',
        label: 'Deconturi',
        group: 'Firmă & oameni',
        permissions: ['hr.deconturi:read', 'hr.deconturi:write'],
    },
    {
        key: 'hr_ordine',
        label: 'Ordine de deplasare',
        group: 'Firmă & oameni',
        permissions: ['hr.ordine_deplasare:read', 'hr.ordine_deplasare:write'],
    },
    // Setări
    {
        key: 'company',
        label: 'Profil firmă',
        group: 'Setări',
        permissions: ['settings.company:read', 'settings.company:write'],
    },
    {
        key: 'products',
        label: 'Catalog produse',
        group: 'Setări',
        permissions: ['settings.products:read', 'settings.products:write'],
    },
    {
        key: 'cost_centers',
        label: 'Centre de cost',
        group: 'Setări',
        permissions: ['settings.cost_centers:read', 'settings.cost_centers:write'],
    },
    {
        key: 'expense_categories',
        label: 'Categorii cheltuieli',
        group: 'Setări',
        permissions: ['settings.expense_categories:read', 'settings.expense_categories:write'],
    },
    {
        key: 'promo_instruments',
        label: 'Instrumente de promovare',
        group: 'Setări',
        permissions: ['settings.promo_instruments:read', 'settings.promo_instruments:write'],
    },
    {
        key: 'sales_channels',
        label: 'Canale vânzare',
        group: 'Setări',
        permissions: ['settings.sales_channels:read', 'settings.sales_channels:write'],
    },
    {
        key: 'filters',
        label: 'Filtre email',
        group: 'Setări',
        permissions: ['settings.filters:read', 'settings.filters:write', 'settings.filters:simulate'],
    },
    {
        key: 'email',
        label: 'SMTP / IMAP',
        group: 'Setări',
        permissions: ['settings.email:read', 'settings.email:write'],
    },
];
const PAGE_BY_KEY = new Map(PAGES.map((p) => [p.key, p]));
export function isPageKey(k) {
    return typeof k === 'string' && PAGE_BY_KEY.has(k);
}
/**
 * Se poate da unui om anume, peste rolul lui?
 *
 * Trei conditii, fiecare cu motivul ei:
 * — sa existe in catalog (un rand ramas in baza de la o permisiune stearsa nu inseamna nimic);
 * — sa nu fie citire: citirile se dau prin acces la pagina, iar doua mecanisme pentru acelasi
 *   lucru inseamna ca nimeni nu mai stie pe care sa se uite;
 * — sa nu fie pe lista blocata: acolo stau si cele prin care cineva si-ar putea largi singur
 *   accesul pana la admin.
 */
export function estePermisiuneAcordabila(p) {
    if (!(p in PERMISSIONS))
        return false;
    if (p.endsWith(':read'))
        return false;
    return !BLOCKED_PERMISSIONS.includes(p);
}
/**
 * Permisiunile care se acordă doar de cine le are deja. Vezi `PERMISIUNI_PERSONALE`.
 *
 * NU e „type guard": negarea unuia pe un `Permission[]` ar îngusta restul la `never`, iar
 * filtrarea „tot ce NU e personal" — exact ce face blanketul adminului — ar da o listă goală.
 */
export function estePermisiunePersonala(p) {
    return PERMISIUNI_PERSONALE.includes(p);
}
/** Reuniunea permisiunilor pentru un set de pagini (chei necunoscute ignorate). */
export function permissionsForPages(keys) {
    const out = new Set();
    for (const k of keys) {
        const p = PAGE_BY_KEY.get(k);
        if (p)
            for (const perm of p.permissions)
                out.add(perm);
    }
    return [...out];
}
//# sourceMappingURL=pages.js.map